import { useState } from "react";
import { Button, Message, Modal } from "semantic-ui-react";
import { Link, useLocation } from "react-router";

import API_service from "xAppLib/providers/API_service";
import instcons_global_model_base from "models/instcons_global_model_base";
import Alert from "../NUI/Alert";
import { AsyncButton } from "../UIelems/AsyncButton";
import { ContactUs } from "@iscr/xAppLib/UIelems/ContactUs";
import { from_melb_ui_tm } from "helpers/datetime";
import { rejectOnResErr } from "../icosm/treatment/utils";
import { useAsyncUpdate } from "xAppLib/Hooks/useAsync";
import { useProfile } from "xAppLib/Hooks";
import { useSubscription } from "xAppLib/Hooks/useSubscription";
import { v4 as uuidv4 } from 'uuid';
import { CancelReasonSelector } from "../ordering/Consults/components/CancelConsult";

export function CouldNotCallConsults() {
	const [prof] = useProfile();

	return (prof?.hist ?? [])
		.filter(rec => rec.status === 'could_not_call')
		.map(rec => <CouldNotCallConsult key={rec.sid} rec={rec}/>);
}

function CouldNotCallConsult({rec}) {
	const {pathname} = useLocation();

	const {sid, spd_hash__mobile: mobilehash = '_', spd_data} = rec;
	const prefix = `/docconsreq`;
	const manage_href = `${prefix}/${sid}/${mobilehash}`;
	const consult_date = from_melb_ui_tm(rec.add_tm).format('MMM D, YYYY');
	const pt_name = app.user?.profs?.length > 1 && (
		<>{spd_data?.first_name} {spd_data?.last_name} – </>
	);

	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [cancelReasonState, setCancelReasonState] = useState({ cancelReason: '', cancelOtherText: '', isValid: false });

	const action = useAsyncUpdate(
		async (act, extra) => await API_service.load_data('doccons_req', {act, sid, mobilehash, ...extra}).then(rejectOnResErr),
	);

	const callAgain = async () => {
		await action.fn('recall_same_number');
		app.trigger(
			app.events.SHOW_MESSAGE,
			'Your call back request has been received',
			'We will try to call you again shortly.',
			'positive',
			`doccons_recall_same_number`
		);
	};

	const cancelConsult = async () => {
		await action.fn('cancel_consult', {
			cancel_reason: cancelReasonState.cancelReason,
			...(cancelReasonState.cancelReason === 'Other' && { cancel_other_text: cancelReasonState.cancelOtherText }),
			cancel_initiated_by: 'patient',
		});
		setCancelModalOpen(false);
		app.trigger(
			app.events.SHOW_MESSAGE,
			'Your consultation has been cancelled',
			'Any payment made will be refunded.',
			'positive',
			`doccons_cancel_consult_${uuidv4()}`
		);
	};

	const {data: frdb} = useSubscription(onData => instcons_global_model_base.watch_record(sid, onData), [sid]);

	if (frdb?.status !== 'could_not_call') {
		return null;
	}

	if (pathname.startsWith(prefix)) {
		return null;
	}

	const error = action.error?.message ?? action.error?.error ?? action.error;

	return (
		<Message info>
			<Message.Header>
				<div className="flex flex-col space-y-2">
					<span>Consultation Booking</span>
					<small className="dates uppercase">
						{pt_name} {consult_date}
					</small>
				</div>
			</Message.Header>

			<Message.Content className="mt-2 space-y-4">
				<p>We're sorry we haven't been able to get in touch with you.</p>
				<p>Please choose from the options below to let us know if you'd still like one of our doctors to call you.</p>

				<div className="flex flex-col md:flex-row gap-2">
					<AsyncButton disabled={action.loading} onClick={callAgain} primary data-testid="call-again-action">
						Re-join the queue
					</AsyncButton>

					<AsyncButton disabled={action.loading} onClick={() => setCancelModalOpen(true)} negative basic data-testid="cancel-consult-action">
						Cancel consultation
					</AsyncButton>

					<Button to={manage_href} disabled={action.loading} as={Link} basic data-testid="manage-consult-action">
						Consultation details
					</Button>
				</div>

				{action.error && (
					<Alert error header="Something went wrong" testId="return-to-wr-error">
						<div className="space-y-4">
							{Boolean(error) && <div>{String(error)}</div>}
							<div>Please try again, or <ContactUs/> if issues persist.</div>
						</div>
					</Alert>
				)}
			</Message.Content>

			<Modal open={cancelModalOpen} onClose={() => setCancelModalOpen(false)} size="small">
				<Modal.Header>Cancel Consultation</Modal.Header>
				<Modal.Content style={{ overflow: 'visible' }}>
					<CancelReasonSelector onChange={setCancelReasonState} />
				</Modal.Content>
				<Modal.Actions className="flex flex-col sm:flex-row">
					<Button basic className="!w-full !m-0" onClick={() => setCancelModalOpen(false)}>Go back</Button>
					<AsyncButton negative disabled={!cancelReasonState.isValid || action.loading} onClick={cancelConsult} data-testid="confirm-cancel-action" className="!w-full !m-0">
						Confirm cancellation
					</AsyncButton>
				</Modal.Actions>
			</Modal>
		</Message>
	);
}
